import { withNamespaces } from 'react-i18next';
import {
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    Col,
    Row,
} from 'reactstrap';

import DateUtils from 'src/services/utils/DateUtils';
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';

import { getVerificationStatusConfig } from '../../constants/verificationStatusConfig';
import { getRiskGaugeImage } from '../../constants/riskGaugeImages';
import useSupplierOverviewAiSummary from './hooks/useSupplierOverviewAiSummary';

const AI_SUMMARY_DISCLAIMER = 'This content was generated by AI and may contain inaccuracies. Please verify critical information independently before making business decisions.';

const VerificationStatusBadge = ({
    status,
    t,
}) => {
    const config = getVerificationStatusConfig(status);

    return (
        <span
            className="overview-ai-summary__verification-status"
            style={{
                color: config.color,
                backgroundColor: config.backgroundColor,
            }}
        >
            <i className={`${config.icon} me-1`} />
            {t(config.label)}
        </span>
    );
};

const AiSummarySkeleton = () => (
    <div className="mb-4">
        <div className="dt-field dt-skeleton mb-3" style={{ width: '100%', height: '72px' }} />
        <div className="dt-field dt-skeleton mb-2" style={{ width: '100%', height: '280px' }} />
        <div className="dt-field dt-skeleton" style={{ width: '100%', height: '48px' }} />
    </div>
);

const AiSummary = ({
    t,
    supplierId,
}) => {
    const dateUtils = new DateUtils();

    const fetchAiSummaryQuery = useSupplierOverviewAiSummary({
        supplierId,
        t,
    });

    if (fetchAiSummaryQuery.isLoading || fetchAiSummaryQuery.isFetching) {
        return <AiSummarySkeleton />;
    }

    if (fetchAiSummaryQuery.isError || !fetchAiSummaryQuery.data) {
        return null;
    }

    const {
        header = {},
        summary = {},
        riskAnalysis = {},
        verification = {},
        lastUpdated,
    } = fetchAiSummaryQuery.data;

    const {
        reportType,
        thirdPartyName,
        overallRiskScore = {},
        createdOn,
    } = header;

    const paragraphs = summary.paragraphs || [];
    const verificationItems = verification.items || [];
    const riskGaugeImage = getRiskGaugeImage(riskAnalysis.rating);

    return (
        <Card className="overview-ai-summary mb-4">
            <CardHeader className="overview-ai-summary__header">
                <Row className="g-3 align-items-center">
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-file-text-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">{t('Report Type')}</small>
                                <div className="overview-ai-summary__meta-value">{t(reportType)}</div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-building-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">{t('Third Party')}</small>
                                <div className="overview-ai-summary__meta-value">{thirdPartyName}</div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-shield-check-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">{t('Overall Risk Score')}</small>
                                <div
                                    className="overview-ai-summary__meta-value"
                                    style={{ color: overallRiskScore.color }}
                                >
                                    {overallRiskScore.title}
                                </div>
                            </div>
                        </div>
                    </Col>
                    <Col xs="12" md="6" lg="3">
                        <div className="overview-ai-summary__meta-item">
                            <i className="ri-calendar-line overview-ai-summary__meta-icon" />
                            <div>
                                <small className="overview-ai-summary__meta-label">{t('Created')}</small>
                                <div className="overview-ai-summary__meta-value">
                                    {dateUtils.convertDateToDate(createdOn, INTERNATIONAL_DATE_FORMAT)}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </CardHeader>

            <CardBody>
                <Row className="g-4">
                    <Col xs="12" lg="7">
                        <h6 className="overview-section-title mb-3">{t('AI Summary')}</h6>
                        <div className="overview-ai-summary__text">
                            {paragraphs.map((paragraph, index) => (
                                <p key={index} className="text-muted mb-3">
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                    </Col>

                    <Col xs="12" lg="5">
                        <div className="overview-ai-summary__risk-analysis mb-4">
                            <h6 className="overview-section-title mb-3 text-center">{t('Risk Analysis by AI')}</h6>
                            <div className="overview-ai-summary__gauge-image">
                                <img
                                    src={riskGaugeImage}
                                    alt={t('Risk Analysis by AI')}
                                />
                            </div>
                            <div
                                className="overview-ai-summary__risk-level-label"
                                style={{ color: riskAnalysis.color }}
                            >
                                {t(riskAnalysis.title)}
                            </div>
                        </div>

                        <div className="overview-ai-summary__verification">
                            <h6 className="overview-section-title mb-3">{t('Company Verification')}</h6>
                            <ul className="list-unstyled mb-0">
                                {verificationItems.map((item, index) => (
                                    <li
                                        key={`${item.label}-${index}`}
                                        className="overview-ai-summary__verification-item"
                                    >
                                        <span className="overview-ai-summary__verification-label">
                                            {t(item.label)}
                                        </span>
                                        <VerificationStatusBadge
                                            status={item.status}
                                            t={t}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Col>
                </Row>
            </CardBody>

            <CardFooter className="overview-ai-summary__footer">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
                    <small className="text-muted mb-0">
                        <i className="ri-information-line me-1" />
                        {t(AI_SUMMARY_DISCLAIMER)}
                    </small>
                    <small className="text-muted mb-0">
                        {t('Last updated')}{' '}
                        {dateUtils.convertDateToDate(lastUpdated, INTERNATIONAL_DATE_FORMAT)}{' '}
                        {dateUtils.convertDateToDate(lastUpdated, 'HH:mm')}
                    </small>
                </div>
            </CardFooter>
        </Card>
    );
};

export default withNamespaces()(AiSummary);
